import React from 'react';
import { Check, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { cn } from '../ui/utils';
import type {
  PerformanceCalibration,
  PerformanceRatingLevel,
  PerformanceResultRow,
  PerformanceSchedule,
} from '../../types/performance.types';
import { performanceApi } from '../../utils/performance-api';
import { formatPerformanceDateTime, formatScore, performancePillClass } from '../../utils/performance';

interface CalibrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule: PerformanceSchedule;
  result: PerformanceResultRow | null;
  levels: PerformanceRatingLevel[];
  calibrations: PerformanceCalibration[];
  onSaved: () => Promise<void> | void;
}

const scoreOf = (row: PerformanceResultRow, ratings: Record<number, number>) => {
  let weighted = 0;
  let total = 0;
  row.goals.forEach((goal) => {
    const rating = ratings[goal.id];
    if (!rating) return;
    weighted += rating * goal.weight;
    total += goal.weight;
  });
  return total ? Math.round((weighted / total) * 100) / 100 : null;
};

export const CalibrationDialog: React.FC<CalibrationDialogProps> = ({
  open,
  onOpenChange,
  schedule,
  result,
  levels,
  calibrations,
  onSaved,
}) => {
  const [ratings, setRatings] = React.useState<Record<number, number>>({});
  const [reason, setReason] = React.useState('');
  const [override, setOverride] = React.useState(false);
  const [overrideScore, setOverrideScore] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [expandedVersion, setExpandedVersion] = React.useState<number | null>(null);

  const history = React.useMemo(
    () =>
      result
        ? calibrations
            .filter((row) => row.userId === result.user.id)
            .sort((a, b) => b.version - a.version)
        : [],
    [calibrations, result]
  );

  React.useEffect(() => {
    if (!open || !result) return;
    const last = history[0];
    const next: Record<number, number> = {};
    result.goals.forEach((goal) => {
      const value = last?.ratings?.[goal.id] ?? goal.managerRating;
      if (value) next[goal.id] = Number(value);
    });
    setRatings(next);
    setReason('');
    setOverride(false);
    setOverrideScore('');
    setExpandedVersion(null);
  }, [open, result, history]);

  if (!result) return null;

  const live = override && overrideScore !== '' ? Number(overrideScore) : scoreOf(result, ratings);
  const base = history[0]?.ratings || Object.fromEntries(
    result.goals.filter((goal) => goal.managerRating).map((goal) => [goal.id, goal.managerRating])
  );
  const hasChanges =
    result.goals.some((goal) => Number(base[goal.id]) !== Number(ratings[goal.id])) || override;
  const readOnly = schedule.status !== 'Calibration';

  const save = async () => {
    if (!reason.trim()) {
      toast.error('A reason is required for every calibration');
      return;
    }
    if (!hasChanges) {
      toast.error('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await performanceApi.calibration.save(schedule.id, result.user.id, {
        ratings,
        reason: reason.trim(),
        overrideScore: override && overrideScore !== '' ? Number(overrideScore) : null,
      });
      toast.success('Calibration saved — the manager’s original rating is kept');
      await onSaved();
      setReason('');
      setOverride(false);
      setOverrideScore('');
    } finally {
      setSaving(false);
    }
  };

  const restore = async (version: number) => {
    setSaving(true);
    try {
      await performanceApi.calibration.restore(schedule.id, result.user.id, version);
      toast.success(`Restored as v${history[0] ? history[0].version + 1 : 1} — nothing deleted`);
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] sm:max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Calibrate · {result.user.name}</DialogTitle>
          <DialogDescription>
            {result.user.jobRole}
            {result.user.department ? ` · ${result.user.department}` : ''}
            {result.user.managerName ? ` · Manager ${result.user.managerName}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2 lg:grid-cols-[minmax(0,1fr)_270px]">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card p-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Live score
                </p>
                <p className="text-2xl font-semibold tabular-nums">{formatScore(live)}</p>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                <p>Manager original {formatScore(result.managerScore)}</p>
                <p>Self {formatScore(result.selfScore)}</p>
              </div>
            </div>

            <div className="space-y-2">
              {result.goals.map((goal) => {
                const value = ratings[goal.id] ?? null;
                const changed = Number(goal.managerRating) !== Number(value);
                return (
                  <div key={goal.id} className="rounded-xl border border-border bg-card p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold">{goal.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {goal.weight}% weight · self {goal.selfRating ?? '–'}
                        </p>
                      </div>
                      {changed ? (
                        <span className="rounded-full border border-warning-border bg-warning-bg px-2 py-0.5 text-xs font-medium text-warning">
                          {goal.managerRating ?? '–'} → {value ?? '–'}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-2 grid grid-cols-5 gap-1">
                      {[...levels]
                        .sort((a, b) => a.score - b.score)
                        .map((level) => (
                          <button
                            key={level.score}
                            type="button"
                            disabled={readOnly}
                            onClick={() =>
                              setRatings((current) => ({ ...current, [goal.id]: level.score }))
                            }
                            className={cn(
                              'rounded-md border px-1 py-1.5 text-center text-sm font-semibold tabular-nums transition-colors',
                              value === level.score
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-card hover:bg-accent',
                              readOnly && 'cursor-default opacity-60'
                            )}
                            title={level.label}
                          >
                            {level.score}
                          </button>
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {!readOnly ? (
              <div className="space-y-3 rounded-xl border border-border bg-card p-4">
                <div className="space-y-1.5">
                  <Label htmlFor="calibration-reason">Reason for the change</Label>
                  <Textarea
                    id="calibration-reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    rows={2}
                    placeholder="Why is this calibration needed?"
                  />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">Override the overall score</p>
                    <p className="text-xs text-muted-foreground">
                      Use only when the weighted result misrepresents the year.
                    </p>
                  </div>
                  <Switch
                    checked={override}
                    onCheckedChange={(checked) => {
                      setOverride(checked);
                      if (!checked) setOverrideScore('');
                    }}
                  />
                </div>
                {override ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={1}
                      max={5}
                      step={0.01}
                      value={overrideScore}
                      onChange={(event) => setOverrideScore(event.target.value)}
                      className="w-28 tabular-nums"
                    />
                    <span className="text-xs text-muted-foreground">1.00 – 5.00</span>
                  </div>
                ) : null}
                <Button onClick={save} disabled={saving || !hasChanges || !reason.trim()}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {override ? 'Save override' : 'Save calibration'}
                </Button>
              </div>
            ) : null}
          </div>

          <aside className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Version history
            </p>
            <div className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-start gap-2">
                <span className="mt-1 h-2 w-2 rounded-full bg-muted-foreground/40" />
                <div>
                  <p className="text-sm font-semibold">Manager's original</p>
                  <p className="text-xs text-muted-foreground">v0</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Score {formatScore(result.managerScore)}
                  </p>
                </div>
              </div>
            </div>

            {history.map((version) => {
              const vScore =
                version.overrideScore != null
                  ? Number(version.overrideScore)
                  : scoreOf(result, version.ratings as Record<number, number>);
              return (
                <div key={version.id} className="rounded-xl border border-border bg-card p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">v{version.version}</p>
                      <p className="text-xs text-muted-foreground">
                        {version.createdByName || 'HR'} · {formatPerformanceDateTime(version.createdAt)}
                      </p>
                    </div>
                    <span className="rounded-full border border-border px-2 py-0.5 text-xs tabular-nums">
                      {formatScore(vScore)}
                    </span>
                  </div>
                  {version.changes.length ? (
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                      {version.changes.join(' · ')}
                    </p>
                  ) : null}
                  <p className="mt-1 text-xs text-muted-foreground">{version.reason}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setExpandedVersion((current) =>
                          current === version.version ? null : version.version
                        )
                      }
                    >
                      {expandedVersion === version.version ? 'Hide' : 'View'}
                    </Button>
                    {!readOnly && version.version !== history[0]?.version ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => restore(version.version)}
                        disabled={saving}
                      >
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
                      </Button>
                    ) : null}
                  </div>
                  {expandedVersion === version.version ? (
                    <div className="mt-2 space-y-1 border-t border-border pt-2">
                      {result.goals.map((goal) => (
                        <div key={goal.id} className="flex items-center justify-between gap-2 text-xs">
                          <span className="truncate text-muted-foreground">{goal.title}</span>
                          <span className="tabular-nums">{version.ratings?.[goal.id] ?? '–'}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {!history.length ? (
              <p className="text-xs text-muted-foreground">
                No calibration yet. The manager's original ratings are the score of record.
              </p>
            ) : null}
          </aside>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
